// Explicit file, not the block's directory: consumers on `moduleResolution:
// nodenext` (Connect) cannot resolve a directory import, and the failure is
// quiet — the types degrade to `error` and everything downstream of this block
// silently becomes `any`.
import {
  beamDur,
  beamPhase,
} from '../guest-profile-fanin/guest-profile-fanin'

/**
 * The three figures in the announcement band.
 *
 * Drawn to the diagram spec, not decorated: sharp corners (radius 0), 1px
 * hairlines, understated line-art, and the brand gradient rationed to the
 * travelling packets and one mark per figure. Everything else is `currentColor`
 * at low opacity, so each figure inherits the band's ink rather than carrying a
 * colour of its own.
 *
 * They live at 96 x 64. An earlier pass drew them at 64 x 40 and they turned to
 * mush: the detail that makes a figure legible needs room, and below about 90px
 * across there is none. If one ever has to shrink, drop marks — never scale the
 * whole thing down and hope.
 *
 * ── THE BEAMS ARE THE HOMEPAGE'S BEAMS ──────────────────────────────────────
 *
 * Signals arrive the way they arrive on the brand pane opposite and on the
 * marketing site's hero: a NEUTRAL hairline wire with a small gradient packet
 * travelling along it. That is `.gpf-beam-d` from the design system's stylesheet
 * plus `beamDur`/`beamPhase` from the fan-in block — the real thing, shared, not
 * a second implementation of the same idea that drifts from it. It also means
 * `prefers-reduced-motion` already freezes them, because the package's own
 * reduced-motion block names those classes.
 *
 * Consequence worth knowing: `stroke-dashoffset` animates UPWARD, which walks
 * each packet toward the path's START. So every beam is drawn
 * DESTINATION-FIRST — `M` at the thing being fed, ending at the source. Draw one
 * source-first and the packet runs backwards.
 *
 * ── GRADIENTS ───────────────────────────────────────────────────────────────
 *
 * Each gradient spans the extent of the ONE shape it paints (`x1`/`x2` in user
 * space). A single frame-wide ramp shared by everything looks right on a
 * full-width strip and wrong on a 16px stub, which samples only the amber end
 * and renders as a flat orange block — that was the bug.
 *
 * `userSpaceOnUse`, never `objectBoundingBox`: a dead-horizontal path has a
 * zero-height bounding box, so a bbox gradient degenerates and the stroke simply
 * does not paint. Opacity likewise goes on `strokeOpacity` / `fillOpacity`, never
 * the `opacity` attribute, on any shape that both fills and strokes — a group
 * opacity makes the fill translucent and the ticket's notches stop occluding the
 * line they are meant to cut.
 *
 * Raw hex in the `<stop>`s is the one place the design law allows it: Safari will
 * not resolve a `var()` inside an SVG gradient stop. They equal `--brand-hot` /
 * `--brand-warm` / `--brand-glow`.
 *
 * For the same family of reasons, the canvas fills that OCCLUDE things (the pass
 * over the wire tails, the notches over the perforation) go through `style`
 * rather than a `fill="var(--background)"` presentation attribute. Custom
 * properties in SVG presentation attributes have a patchy WebKit history, and
 * the failure mode here is not cosmetic — an unresolved fill paints nothing, so
 * the beams would run visibly through the pass and the perforation straight
 * through its notches. Inline `style` is CSS proper and resolves everywhere.
 */

const STROKE = 'currentColor'

/** The brand ramp, spanning whatever it is asked to span. */
function Beam({ id, x1, x2 }: { id: string; x1: number; x2: number }) {
  return (
    <linearGradient
      gradientUnits="userSpaceOnUse"
      id={id}
      x1={x1}
      x2={x2}
      y1="0"
      y2="0"
    >
      <stop offset="0%" stopColor="#ff2d87" />
      <stop offset="55%" stopColor="#ff6b4a" />
      <stop offset="100%" stopColor="#ffa234" />
    </linearGradient>
  )
}

/**
 * One wire: the neutral hairline, then the travelling packet on the same path.
 * `index` only varies the timing, so no two wires in a figure ever sync up.
 */
function Wire({
  d,
  gradientId,
  index,
}: {
  d: string
  gradientId: string
  index: number
}) {
  const duration = beamDur(index)

  return (
    <>
      <path d={d} stroke={STROKE} strokeOpacity="0.22" strokeWidth="1" />
      <path
        className="gpf-beam-d"
        d={d}
        pathLength={100}
        stroke={`url(#${gradientId})`}
        strokeLinecap="round"
        strokeWidth="1.6"
        style={{
          ['--gpf-dur' as string]: `${duration.toFixed(2)}s`,
          animationDelay: `-${beamPhase(index, duration).toFixed(2)}s`,
        }}
      />
    </>
  )
}

const svgProps = {
  'aria-hidden': true,
  className: 'h-16 w-24 shrink-0',
  fill: 'none',
  viewBox: '0 0 96 64',
  xmlns: 'http://www.w3.org/2000/svg',
} as const

/**
 * The QR the door scans, as the squares to draw.
 *
 * A fixed, hand-set module map rather than anything generated: it has to be
 * byte-identical on the server and the client, and it has to carry the corner
 * finder squares, which are what make a QR read as a QR rather than as noise.
 *
 * Centred under the pass's fields the way a real wallet pass sits its code —
 * 7 modules on a 2.6 pitch is 18.2 wide, inside a 50-wide card. Resolved once
 * at module scope, not per render.
 */
const QR_ORIGIN = { x: 58, y: 39 }
const QR_PITCH = 2.6
const QR_MAP = [
  [1, 1, 1, 0, 1, 1, 1],
  [1, 0, 1, 0, 1, 0, 1],
  [1, 1, 1, 1, 1, 1, 1],
  [0, 0, 1, 1, 0, 1, 0],
  [1, 1, 1, 0, 1, 1, 0],
  [1, 0, 1, 1, 0, 0, 1],
  [1, 1, 1, 0, 1, 1, 1],
]

/**
 * The map, placed. `origin` is the top-left module, `pitch` the module stride.
 *
 * A function rather than the one pre-placed constant it used to be: three
 * figures now draw a code, at different sizes, and offsetting a hard-coded set
 * of coordinates by hand (`x - 45`) made every one of them unreadable to anyone
 * trying to work out where the code actually sits.
 */
function qrModules(origin: { x: number; y: number }, pitch: number) {
  return QR_MAP.flatMap((row, rowIndex) =>
    row.flatMap((on, colIndex) => {
      if (!on) return []
      const x = origin.x + colIndex * pitch
      const y = origin.y + rowIndex * pitch
      return [{ key: `${String(x)}:${String(y)}`, x, y }]
    })
  )
}

/**
 * A 7-module code of this pitch, centred in a box, already placed.
 *
 * One call rather than an origin helper feeding a module helper: the two-step
 * version shipped with the pitch missing from the first step, which made every
 * x NaN. Everything a placed code needs is in one argument list here, so that
 * particular mistake cannot be made again.
 */
function centredQr(
  box: { x: number; width: number },
  y: number,
  pitch: number
) {
  return qrModules({ x: box.x + (box.width - 7 * pitch) / 2, y }, pitch)
}

const QR_MODULES = qrModules(QR_ORIGIN, QR_PITCH)

/**
 * Wallet booking confirmations: the booking and the guest's loyalty converging
 * into ONE pass.
 *
 * The same argument the homepage hero makes, at figure scale — many signals, one
 * owned record — because that is the shape of the product and this feature is an
 * instance of it. The payoff is the CARD, so the card is filled and detailed: a
 * venue brand strip, the venue, the date and time, and the QR the door scans.
 *
 * A previous pass drew the pass alone with a wide barcode across it, which read
 * as a generic loyalty card and made no argument at all.
 */
export function PassArt() {
  /* Destination-first, so the packets travel INTO the pass. They start a few
     units INSIDE the card, which the card's own fill then covers: ended exactly
     on its edge, the two streams pile up at the convergence and read as a
     smudge sitting outside the card. */
  const wires = ['M48 32 C 34 32, 24 15, 9 15', 'M48 32 C 34 32, 24 49, 9 49']

  return (
    <svg {...svgProps}>
      <defs>
        <Beam id="ann-pass-wire" x1={9} x2={48} />
        <Beam id="ann-pass-strip" x1={42} x2={92} />
      </defs>

      {wires.map((d, i) => (
        <Wire d={d} gradientId="ann-pass-wire" index={i} key={d} />
      ))}
      {/* the two sources. Small: at 96 across, the website's 6px signal dot
          scales to about this, and anything larger reads as a bead on a string
          rather than the tail of a wire. */}
      <circle cx="9" cy="15" fill="url(#ann-pass-wire)" r="1.3" />
      <circle cx="9" cy="49" fill="url(#ann-pass-wire)" r="1.3" />

      {/* the pass behind: a wallet holds more than one */}
      <rect
        fill={STROKE}
        fillOpacity="0.07"
        height="16"
        stroke={STROKE}
        strokeOpacity="0.2"
        width="42"
        x="46"
        y="1"
      />

      {/* the confirmation itself, filled so it occludes both the stack behind
          and the tails of the two wires */}
      <rect
        style={{ fill: 'var(--background)' }}
        height="56"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="50"
        x="42"
        y="4"
      />
      <rect fill="url(#ann-pass-strip)" height="5" width="50" x="42" y="4" />

      {/* the venue, then the date and the time */}
      <path
        d="M47 18h22"
        stroke={STROKE}
        strokeOpacity="0.5"
        strokeWidth="1.8"
      />
      <path
        d="M47 26h15M47 32h10"
        stroke={STROKE}
        strokeOpacity="0.4"
        strokeWidth="1.5"
      />

      <path d="M42 36h50" stroke={STROKE} strokeOpacity="0.18" />

      {QR_MODULES.map((module) => (
        <rect
          fill={STROKE}
          fillOpacity="0.34"
          height="2.2"
          key={module.key}
          width="2.2"
          x={module.x}
          y={module.y}
        />
      ))}
    </svg>
  )
}

/** Phone bookings: the call travelling down the line into the diary. */
export function CallArt() {
  return (
    <svg {...svgProps}>
      <defs>
        <Beam id="ann-call" x1={36} x2={60} />
      </defs>

      {/* the line ringing. Two arcs DETACHED from the handset: struck from its
          own edge they close around it and the pair reads as a mug handle, and
          started at the frame edge they clip into broken stubs. Quadratics
          rather than `A`, so the bulge direction is unambiguous. */}
      <path
        d="M11 26 Q 4 32 11 38"
        stroke={STROKE}
        strokeLinecap="round"
        strokeOpacity="0.38"
      />
      <path
        d="M8 22 Q 0 32 8 42"
        stroke={STROKE}
        strokeLinecap="round"
        strokeOpacity="0.2"
      />

      <rect
        height="36"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="20"
        x="16"
        y="14"
      />
      <path d="M21 19h10" stroke={STROKE} strokeOpacity="0.28" />
      <path d="M21 45h10" stroke={STROKE} strokeOpacity="0.18" />

      {/* destination-first, so the packet travels INTO the diary */}
      <Wire d="M60 32 C 52 32, 46 32, 38 32" gradientId="ann-call" index={0} />
      <circle cx="38" cy="32" fill="url(#ann-call)" r="1.3" />

      <rect
        height="48"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="34"
        x="60"
        y="8"
      />
      <path
        d="M60 20h34M60 32h34M60 44h34M77 8v48"
        stroke={STROKE}
        strokeOpacity="0.2"
      />
      {/* the booking the call just made */}
      <rect
        fill={STROKE}
        fillOpacity="0.2"
        height="12"
        width="17"
        x="77"
        y="20"
      />
    </svg>
  )
}

/** Ticketed events: a ticket, with the stub already paid for. */
export function TicketArt() {
  return (
    <svg {...svgProps}>
      <defs>
        {/* spanning the stub alone, so the mark reads as the full pink-to-amber
            ramp rather than the flat orange the frame-wide version sampled */}
        <Beam id="ann-ticket" x1={70} x2={86} />
      </defs>

      <rect
        height="38"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="88"
        x="4"
        y="13"
      />

      {/* the event, named and dated on the body of the ticket */}
      <path
        d="M12 25h32"
        stroke={STROKE}
        strokeOpacity="0.45"
        strokeWidth="1.6"
      />
      <path d="M12 33h40M12 39h24" stroke={STROKE} strokeOpacity="0.22" />

      {/* the perforation, notched out of the ticket's own edges. A solid canvas
          fill on the notches is what cuts the line they sit on. */}
      <path
        d="M64 13v38"
        stroke={STROKE}
        strokeDasharray="2 4"
        strokeOpacity="0.4"
      />
      <circle
        cx="64"
        cy="13"
        style={{ fill: 'var(--background)' }}
        r="4"
        stroke={STROKE}
        strokeOpacity="0.4"
      />
      <circle
        cx="64"
        cy="51"
        style={{ fill: 'var(--background)' }}
        r="4"
        stroke={STROKE}
        strokeOpacity="0.4"
      />

      {/* the stub carries the one accent: payment already taken. It breathes on
          a slow cycle — the single idle pulse the diagram spec allows. */}
      <rect
        className="ann-flash"
        fill="url(#ann-ticket)"
        height="4"
        width="16"
        x="70"
        y="26"
      />
      <path d="M70 35h16M70 40h10" stroke={STROKE} strokeOpacity="0.22" />
    </svg>
  )
}

/**
 * Smart Seating: the couple moves, the party of four gets seated.
 *
 * This is the product's OWN explainer diagram at figure scale. Connect's
 * `bookings/optimisation/how-it-works` animates exactly this scene over ten
 * seconds — a couple on the window four-top, a party of four turned away, the
 * couple quietly moved to a two-seater, both parties seated — and the whole
 * feature is that one move. Drawing something else here would teach a customer
 * a second story about the same thing.
 *
 * The composition is that diagram's: the four-top top-left, the two-seaters
 * bottom-right, one of them occupied throughout because the point is that it
 * moves what it must and leaves the rest of the room alone.
 *
 * TWO STROKES, TWO MEANINGS, and keeping them apart is what makes the figure
 * readable at 96px:
 *
 *   • The MOVE is a dashed neutral arc with an arrowhead. It is an instruction
 *     the venue carries out, not a signal, and it costs the venue nothing.
 *   • The ARRIVAL is the brand beam, travelling into the four-top. It is the
 *     booking that would otherwise have gone elsewhere, which is the only thing
 *     on this figure a venue is actually being offered.
 *
 * An earlier pass beamed the MOVE and drew no arrival at all. It read as "a
 * thing slid across a floor plan" and made no argument, because the party being
 * won was never on the page.
 */
export function SeatArt() {
  return (
    <svg {...svgProps}>
      <defs>
        <Beam id="ann-seat" x1={40} x2={92} />
      </defs>

      {/* destination-first, so the booking travels INTO the four-top */}
      <Wire
        d="M36 19 C 52 12, 64 27, 90 20"
        gradientId="ann-seat"
        index={0}
      />
      <circle cx="90" cy="20" fill="url(#ann-seat)" r="1.3" />

      {/* the couple's move, before the tables so both fills trim its ends */}
      <path
        d="M22 34 C 26 42, 34 46, 44 48"
        stroke={STROKE}
        strokeDasharray="3 2.5"
        strokeOpacity="0.34"
      />
      <path
        d="M40 45.5l4.6 2.6l-4.6 2.6"
        stroke={STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeOpacity="0.34"
      />

      {/* the four-top, filled so it occludes the beam's tail and the arc's
          start — the party of four that had nowhere to sit, now on it */}
      <rect
        style={{ fill: 'var(--background)' }}
        height="26"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="36"
        x="4"
        y="8"
      />
      <circle cx="13" cy="16" fill={STROKE} fillOpacity="0.34" r="3.1" />
      <circle cx="31" cy="16" fill={STROKE} fillOpacity="0.34" r="3.1" />
      <circle cx="13" cy="26" fill={STROKE} fillOpacity="0.34" r="3.1" />
      <circle cx="31" cy="26" fill={STROKE} fillOpacity="0.34" r="3.1" />

      {/* the two-seater the couple lands on */}
      <rect
        style={{ fill: 'var(--background)' }}
        height="18"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="24"
        x="46"
        y="40"
      />
      <circle cx="53" cy="49" fill={STROKE} fillOpacity="0.34" r="3.1" />
      <circle cx="63" cy="49" fill={STROKE} fillOpacity="0.34" r="3.1" />

      {/* occupied all along, and never touched */}
      <rect
        height="18"
        stroke={STROKE}
        strokeOpacity="0.2"
        width="18"
        x="74"
        y="40"
      />
      <circle cx="80" cy="49" fill={STROKE} fillOpacity="0.16" r="2.8" />
      <circle cx="88" cy="49" fill={STROKE} fillOpacity="0.16" r="2.8" />
    </svg>
  )
}

/**
 * Table ordering: the code on the table, and the order that lands at the till.
 *
 * Composed source-left / destination-right like the call and the pass, because
 * that IS the shape of it: the table is where the order starts and the till is
 * what it becomes. The table is drawn from above with its covers, so it reads
 * as a table rather than as another card, and the QR reuses the pass's module
 * map — one hand-set code in this file, used wherever a figure needs one.
 *
 * The accent is the table number on the docket. Of everything this feature
 * does, that is the part a venue cares about: the order arrives already knowing
 * where it goes.
 */
export function OrderArt() {
  return (
    <svg {...svgProps}>
      <defs>
        <Beam id="ann-order-wire" x1={42} x2={64} />
        <Beam id="ann-order-chip" x1={68} x2={80} />
      </defs>

      {/* destination-first, so the order travels INTO the docket */}
      <Wire
        d="M64 34 C 56 34, 52 34, 42 34"
        gradientId="ann-order-wire"
        index={0}
      />
      <circle cx="42" cy="34" fill="url(#ann-order-wire)" r="1.3" />

      {/* the covers, struck before the table so its fill trims them to stubs */}
      <path
        d="M13 9v46M31 9v46"
        stroke={STROKE}
        strokeOpacity="0.22"
        strokeWidth="3"
      />

      {/* the table from above, filled so it occludes the wire's tail */}
      <rect
        style={{ fill: 'var(--background)' }}
        height="36"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="34"
        x="5"
        y="14"
      />
      {centredQr({ x: 5, width: 34 }, 24, 2.4).map((module) => (
        <rect
          fill={STROKE}
          fillOpacity="0.34"
          height="2"
          key={`order-${module.key}`}
          width="2"
          x={module.x}
          y={module.y}
        />
      ))}

      {/* the docket, torn off at the pass */}
      <rect
        style={{ fill: 'var(--background)' }}
        height="46"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="28"
        x="64"
        y="6"
      />
      {/* which table it came from */}
      <rect fill="url(#ann-order-chip)" height="5" width="12" x="68" y="11" />
      <path
        d="M68 24h20M68 30h13M68 37h20"
        stroke={STROKE}
        strokeOpacity="0.24"
      />
      <path d="M64 42h28" stroke={STROKE} strokeOpacity="0.18" />
      <path
        d="M68 47h11"
        stroke={STROKE}
        strokeOpacity="0.45"
        strokeWidth="1.8"
      />
      {/* the tear, drawn ON the docket's bottom edge */}
      <path
        d="M64 52l4 3l4-3l4 3l4-3l4 3l4-3l4 3"
        stroke={STROKE}
        strokeOpacity="0.4"
      />
    </svg>
  )
}

/**
 * Square reward vouchers: points on the loyalty card becoming money at the till.
 *
 * Two objects and the conversion between them, which is the whole feature. The
 * left card is the member's points, the right is what they turn into, and the
 * accent is the VALUE — because unlike every other loyalty reward, this one
 * comes off the bill as an amount.
 *
 * The code is a QR, not a linear barcode: what the member is shown is the
 * redemption code a Square till scans, and every code in this product is a QR.
 * A barcode drew a different kind of object — a retail price tag — and quietly
 * said the wrong thing about where the voucher gets used.
 *
 * Still not a second wallet pass: the pass figure owns the brand-strip-and-QR
 * card, so this one leads with the value bar and carries no strip.
 */
export function RewardArt() {
  const points = [
    { cx: 12, cy: 25 },
    { cx: 21, cy: 25 },
    { cx: 30, cy: 25 },
    { cx: 12, cy: 37 },
    { cx: 21, cy: 37 },
  ]

  return (
    <svg {...svgProps}>
      <defs>
        <Beam id="ann-reward-wire" x1={40} x2={56} />
        <Beam id="ann-reward-value" x1={57} x2={77} />
      </defs>

      <Wire
        d="M56 32 C 50 32, 48 32, 40 32"
        gradientId="ann-reward-wire"
        index={0}
      />
      <circle cx="40" cy="32" fill="url(#ann-reward-wire)" r="1.3" />

      {/* the points already earned, and the one still to come */}
      <rect
        style={{ fill: 'var(--background)' }}
        height="36"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="34"
        x="4"
        y="14"
      />
      {points.map((p) => (
        <circle
          cx={p.cx}
          cy={p.cy}
          fill={STROKE}
          fillOpacity="0.3"
          key={`${String(p.cx)}:${String(p.cy)}`}
          r="2.8"
        />
      ))}
      <circle
        cx="30"
        cy="37"
        r="2.8"
        stroke={STROKE}
        strokeDasharray="2 2"
        strokeOpacity="0.3"
      />

      {/* the voucher it converts into */}
      <rect
        style={{ fill: 'var(--background)' }}
        height="52"
        stroke={STROKE}
        strokeOpacity="0.4"
        width="40"
        x="52"
        y="6"
      />
      {/* what it is worth: the figure's one accent */}
      <rect fill="url(#ann-reward-value)" height="6" width="20" x="57" y="13" />
      <path d="M57 26h28M57 31h17" stroke={STROKE} strokeOpacity="0.22" />
      <path d="M52 36h40" stroke={STROKE} strokeOpacity="0.18" />

      {/* the code the till scans */}
      {centredQr({ x: 52, width: 40 }, 40, 2.4).map((module) => (
        <rect
          fill={STROKE}
          fillOpacity="0.34"
          height="2"
          key={`reward-${module.key}`}
          width="2"
          x={module.x}
          y={module.y}
        />
      ))}
    </svg>
  )
}
